import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { API_BASE, PROJECT } from '../../constants/general.constants';
import { ConnectionType } from '../../interfaces';
import { AppStateService } from '../app-state/app-state.service';
import { ConnectionService } from '../connection/connection.service';

interface DeletePrefixResult {
  prefix: string;
  deleted: number;
}

interface SignedUrlResult {
  url: string;
  path: string;
  method: string;
  expirySecs: number;
}

/**
 * Object-storage operations (delete and presigned URLs) exposed to workflow
 * step code (delete: SCRUM-781, presign: EHQ-5).
 *
 * Newton-rs (Arrow Flight) has no delete action — it is a compute service, not a
 * file-management proxy — so delete is surfaced here by calling ehq-server's
 * existing object-storage HTTP delete endpoints. Connection names are resolved
 * to ids locally from the active project's connections (the endpoints are keyed
 * by connection id, but step code passes a connection name).
 *
 * Registered under the ARROW_FLIGHT namespace (alongside listCloud) via
 * additional-methods.constants.ts; bridge methods take an args array and return
 * a Promise, matching the other async bridge services.
 *
 * presignUrl (EHQ-5) is likewise routed here — to ehq-server's HTTP signed-url
 * endpoint — instead of to newton-rs, which required an already-open Arrow
 * Flight session and failed silently when presign was a step's first Flight op.
 */
@Injectable({
  providedIn: 'root'
})
export class ObjectStorageFileService {
  readonly #httpClient = inject(HttpClient);
  readonly #appState = inject(AppStateService);
  readonly #connectionService = inject(ConnectionService);

  /**
   * Resolve an ObjectStorage connection name to its numeric id from the active
   * project's connections. Throws if no matching connection exists.
   */
  #resolveConnectionId(connectionName: string): number {
    if (!connectionName) {
      throw new Error('ObjectStorage connection name must be provided');
    }
    const connection = this.#connectionService
      .connections()
      .find(c => c.name === connectionName && c.type === ConnectionType.ObjectStorage);
    if (!connection?.connectionId) {
      throw new Error(`ObjectStorage connection "${connectionName}" not found`);
    }
    return connection.connectionId;
  }

  #filesUrl(connectionId: number): string {
    const projectId = this.#appState.projectId();
    if (!projectId) {
      throw new Error('No active project; cannot delete object-storage files');
    }
    return `${API_BASE}/${PROJECT}/${projectId}/object-storage/${connectionId}/files`;
  }

  /**
   * Delete a single object.
   * args: [connectionName: string, path: string]
   */
  async deleteCloudObject(args: any[]): Promise<void> {
    const [connectionName, path] = args;
    if (typeof path !== 'string' || path.trim() === '') {
      throw new Error('deleteCloudObject requires a non-empty path');
    }
    const connectionId = this.#resolveConnectionId(connectionName);
    const params = new HttpParams().set('path', path);
    await firstValueFrom(
      this.#httpClient.delete<void>(this.#filesUrl(connectionId), { params })
    );
  }

  /**
   * Delete every object under a prefix. Returns the number of objects deleted.
   * A non-empty prefix is required to avoid wiping the whole connection.
   * args: [connectionName: string, prefix: string]
   */
  async deleteCloudPrefix(args: any[]): Promise<number> {
    const [connectionName, prefix] = args;
    if (typeof prefix !== 'string' || prefix.trim() === '') {
      throw new Error('deleteCloudPrefix requires a non-empty prefix');
    }
    const connectionId = this.#resolveConnectionId(connectionName);
    const params = new HttpParams().set('prefix', prefix);
    const result = await firstValueFrom(
      this.#httpClient.post<DeletePrefixResult>(
        `${this.#filesUrl(connectionId)}/delete-prefix`,
        null,
        { params }
      )
    );
    return result?.deleted ?? 0;
  }

  /**
   * Map the workflow-facing presign method ("read"/"write") to the HTTP
   * signed-url endpoint's method ("Get"/"Put"). The endpoint supports only those
   * two; "delete" (or anything else) is rejected — deleting has its own
   * deleteCloudObject path.
   */
  #toSignedUrlMethod(method?: string): 'Get' | 'Put' {
    const normalized = (method ?? 'read').toString().trim().toLowerCase();
    switch (normalized) {
      case 'read':
      case 'get':
        return 'Get';
      case 'write':
      case 'put':
        return 'Put';
      default:
        throw new Error(
          `presignUrl: unsupported method "${method}". The signed-url endpoint supports only "read" (Get) and "write" (Put); use deleteCloudObject to delete.`
        );
    }
  }

  /**
   * Generate a presigned URL for an object via ehq-server's HTTP signed-url
   * endpoint (EHQ-5). Unlike the former newton-rs SignResource DoAction, this
   * needs no active Arrow Flight session, so a step can presign as its very
   * first Flight operation.
   * args: [connectionName: string, path: string, duration?: number, method?: string]
   * Returns the presigned URL string (same return shape as the old WS path).
   */
  async presignUrl(args: any[]): Promise<string> {
    const [connectionName, path, duration, method] = args;
    if (typeof path !== 'string' || path.trim() === '') {
      throw new Error('presignUrl requires a non-empty path');
    }
    const connectionId = this.#resolveConnectionId(connectionName);
    const httpMethod = this.#toSignedUrlMethod(method);
    let params = new HttpParams().set('path', path).set('method', httpMethod);
    // Only forward a positive expiry; a 0/negative value would presign an
    // already-expired URL. Omitting it lets the server apply its default.
    if (typeof duration === 'number' && duration > 0) {
      params = params.set('expirySecs', String(duration));
    }
    const result = await firstValueFrom(
      this.#httpClient.get<SignedUrlResult>(
        `${this.#filesUrl(connectionId)}/signed-url`,
        { params }
      )
    );
    if (!result?.url) {
      throw new Error(
        `Presign for "${path}" on "${connectionName}" returned no URL`
      );
    }
    return result.url;
  }
}
